import * as React from 'react';
import {
  MdSearch,
  MdClose,
  MdFileDownload,
  MdCheckCircle,
  MdContentCopy,
  MdCheck,
  MdVisibility,
  MdLayers,
  MdFactCheck,
} from 'react-icons/md';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import DataGrid from '../../Components/Table/DataGrid';
import PageTitle from '../../Components/Common/PageTitle';
import SearchableDropdown from '../../Components/Subscription/SearchableDropdown';
import Modal from '../../Components/Common/Modal';
import { useAuth } from '../../Context/AuthContext';
import { getInsuranceList } from '../../Services/Claim360Service';

// Alias name chip; theme tokens so it follows dark mode.
const ALIAS_CHIP = {
  background: 'var(--hc-tint-1)',
  color: 'var(--hc-body-text)',
  borderColor: 'var(--hc-surface-border)',
  padding: '5px 10px',
  fontSize: '0.8rem',
};

const SERVICE_FILTER_OPTIONS = [
  { value: 'All', label: 'All Services' },
  { value: 'eligibility', label: 'Eligibility Check Supported' },
  { value: 'claim_status', label: 'Claim Status Supported' },
  { value: 'claim_submission', label: 'Claim Submission Supported' },
];

export default function InsuranceList() {
  const { token } = useAuth();

  const [search, setSearch] = React.useState('');
  const [serviceFilter, setServiceFilter] = React.useState('All');
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);


  // Modal for viewing all aliases of a selected payer
  const [aliasModalPayer, setAliasModalPayer] = React.useState(null);
  const [aliasModalSearch, setAliasModalSearch] = React.useState('');
  const [copiedAlias, setCopiedAlias] = React.useState(null);

  const loadInsuranceList = React.useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await getInsuranceList(token);
      const payload = response?.data?.insurances || response?.data || response || [];
      const list = Array.isArray(payload) ? payload : [];
      setRows(list);
    } catch (error) {
      console.error('Failed to load insurance list:', error);
      toast.error(error.message || 'Unable to load insurance list.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  React.useEffect(() => {
    loadInsuranceList();
  }, [loadInsuranceList]);

  // Statistics KPI summary matching other list pages
  const stats = React.useMemo(() => {
    const total = rows.length;
    const withAliases = rows.filter((r) => Array.isArray(r.child_names) && r.child_names.length > 0).length;
    const eligibilityCount = rows.filter((r) => r.is_eligibility).length;

    return [
      {
        label: 'Total Payers',
        value: total.toLocaleString(),
        icon: <MdLayers size={22} color="var(--hc-icon-blue-text)" />,
        iconBg: 'var(--hc-icon-blue-bg)',
        accentClass: 'border-primary',
        valueClass: 'text-primary',
      },
      {
        label: 'Payers with Alternate Names',
        value: withAliases.toLocaleString(),
        icon: <MdFactCheck size={22} color="var(--hc-status-success-text)" />,
        iconBg: 'var(--hc-icon-green-bg)',
        accentClass: 'border-success',
        valueClass: 'text-success',
      },
      {
        label: 'Eligibility Supported',
        value: eligibilityCount.toLocaleString(),
        icon: <MdCheckCircle size={22} color="var(--hc-status-info-text)" />,
        iconBg: 'var(--hc-status-info-bg)',
        accentClass: 'border-info',
        valueClass: 'text-info',
      },
    ];
  }, [rows]);

  // Filtered rows
  const filteredRows = React.useMemo(() => {
    const term = search.trim().toLowerCase();

    return rows.filter((row) => {
      // 1. Service filter
      if (serviceFilter === 'eligibility' && !row.is_eligibility) return false;
      if (serviceFilter === 'claim_status' && !row.is_claim_status) return false;
      if (serviceFilter === 'claim_submission' && !row.is_claim_submission) return false;

      // 2. Search query matches parent name, payer ID, or any child name
      if (!term) return true;

      const parentMatch = String(row.parent_name || '').toLowerCase().includes(term);
      const idMatch = String(row.payer_id || '').toLowerCase().includes(term) || String(row.stedi_id || '').toLowerCase().includes(term);
      const childMatch = Array.isArray(row.child_names) && row.child_names.some((c) => String(c || '').toLowerCase().includes(term));

      return parentMatch || idMatch || childMatch;
    });
  }, [rows, search, serviceFilter]);


  // Copy alias text to clipboard
  const handleCopyAlias = (text) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      setCopiedAlias(text);
      toast.success(`Copied "${text}" to clipboard`);
      setTimeout(() => setCopiedAlias(null), 2500);
    }
  };

  // Export to Excel
  const handleExport = () => {
    if (!filteredRows.length) {
      toast.warning('No records to export.');
      return;
    }

    const exportData = filteredRows.map((r, index) => ({
      '#': index + 1,
      'Parent Insurance Name': r.parent_name || '-',
      'Primary Payer ID': r.payer_id || '-',
      'Supported Names (Child Aliases)': (r.child_names && r.child_names.length > 0) ? r.child_names.join(' | ') : (r.parent_name || '-'),
      'Total Aliases': Array.isArray(r.child_names) ? r.child_names.length : 0,
      'Eligibility Supported': r.is_eligibility ? 'Yes' : 'No',
      'Claim Status Supported': r.is_claim_status ? 'Yes' : 'No',
      'Claim Submission Supported': r.is_claim_submission ? 'Yes' : 'No',
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Insurance_List');

    worksheet['!cols'] = [
      { wch: 6 },
      { wch: 35 },
      { wch: 18 },
      { wch: 60 },
      { wch: 15 },
      { wch: 22 },
      { wch: 22 },
      { wch: 26 },
    ];

    XLSX.writeFile(workbook, 'Aegis360_Insurance_List.xlsx');
    toast.success('Insurance catalog exported successfully.');
  };

  // Table columns
  const columns = React.useMemo(() => [
    {
      id: 'parent_name',
      label: 'Parent Insurance Name',
      minWidth: 260,
      format: (value) => (
        <div className="fw-semibold text-truncate" style={{ maxWidth: 320, color: 'var(--hc-text-title)' }} title={value}>
          {value || '-'}
        </div>
      ),
    },
    {
      id: 'payer_id',
      label: 'Payer ID',
      minWidth: 130,
      format: (value) => (
        <span
          className="badge px-2 py-1 font-monospace rounded-2 border"
          style={{ background: 'var(--hc-tint-1)', color: 'var(--hc-text-title)', borderColor: 'var(--hc-surface-border)' }}
        >
          {value || '-'}
        </span>
      ),
    },
    {
      id: 'aliases',
      label: 'Supported Names / Aliases',
      minWidth: 380,
      format: (_v, row) => {
        const names = row.child_names || [];

        if (!names.length) {
          return (
            <span
              className="badge rounded-pill border fw-normal text-start text-truncate"
              style={{ ...ALIAS_CHIP, maxWidth: 340 }}
              title={row.parent_name}
            >
              {row.parent_name}
            </span>
          );
        }

        const visibleNames = names.slice(0, 2);

        return (
          <div className="py-1" style={{ maxWidth: 460 }}>
            <div className="d-flex flex-wrap align-items-center gap-1">
              {visibleNames.map((name, idx) => (
                <span
                  key={idx}
                  className="badge rounded-pill border fw-normal text-start text-truncate"
                  style={{ ...ALIAS_CHIP, maxWidth: 240 }}
                  title={name}
                >
                  {name}
                </span>
              ))}
            </div>

            {names.length > 2 && (
              <div className="mt-1">
                <button
                  type="button"
                  className="btn btn-link p-0 text-decoration-none small d-inline-flex align-items-center gap-1 fw-semibold hc-text-primary"
                  style={{ fontSize: '0.8rem' }}
                  onClick={() => setAliasModalPayer(row)}
                >
                  <MdVisibility size={14} />
                  <span>Show all ({names.length})</span>
                </button>
              </div>
            )}
          </div>
        );
      },
    },
    {
      id: 'services',
      label: 'Supported Services',
      minWidth: 200,
      format: (_v, row) => (
        <div className="d-flex flex-wrap gap-1">
          {row.is_eligibility && (
            <span className="badge bg-success-subtle text-success border border-success-subtle rounded-pill px-2 py-1 small">
              Eligibility
            </span>
          )}
          {row.is_claim_status && (
            <span className="badge bg-primary-subtle text-primary border border-primary-subtle rounded-pill px-2 py-1 small">
              Claim Status
            </span>
          )}
          {row.is_claim_submission && (
            <span className="badge bg-info-subtle text-info border border-info-subtle rounded-pill px-2 py-1 small">
              Submission
            </span>
          )}
          {!row.is_eligibility && !row.is_claim_status && !row.is_claim_submission && (
            <span className="badge bg-light text-muted border rounded-pill px-2 py-1 small">None</span>
          )}
        </div>
      ),
    },
  ], []);

  // Filtered aliases inside the modal
  const modalFilteredAliases = React.useMemo(() => {
    if (!aliasModalPayer || !Array.isArray(aliasModalPayer.child_names)) return [];
    const q = aliasModalSearch.trim().toLowerCase();
    if (!q) return aliasModalPayer.child_names;
    return aliasModalPayer.child_names.filter((name) => String(name).toLowerCase().includes(q));
  }, [aliasModalPayer, aliasModalSearch]);

  return (
    <>
      <PageTitle title="Insurance List - Aegis 360" />

      {/* Header matching EligibilityVerificationList */}
      <div className="d-flex flex-md-row flex-column align-items-start justify-content-between mb-4">
        <div>
          <h4 className="fw-bold mb-1 hc-text-primary fs-3">Insurance List</h4>
          <p className="text-secondary mb-0 small">
            Directory of supported insurance parent payers, clearinghouse IDs, and recognized child alias names.
          </p>
        </div>
        <button
          onClick={handleExport}
          type="button"
          className="btn hc-bg-primary mt-md-0 mt-2 w-md-auto btn-sm text-white fw-semibold d-flex align-items-center justify-content-center gap-1 px-3 py-2 rounded-3 text-nowrap"
          disabled={!rows.length}
        >
          <MdFileDownload size={20} />
          <span>Export Excel</span>
        </button>
      </div>

      {/* KPI Cards matching hc-list-kpi-card */}
      {loading ? (
        <div className="row g-3 mb-3">
          {[1, 2, 3].map((_, i) => (
            <div key={i} className="col-12 col-sm-6 col-lg-4">
              <div className="bg-white p-4 rounded-4 shadow d-flex align-items-center gap-3 h-100">
                <div className="skeleton position-relative overflow-hidden rounded-2" style={{ width: 44, height: 44, borderRadius: 12 }} />
                <div className="w-100">
                  <div className="skeleton mb-2" style={{ height: 16, width: '70%' }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 24, width: '45%' }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="row g-3 mb-3">
          {stats.map((stat) => (
            <div key={stat.label} className="col-12 col-sm-6 col-lg-4">
              <div className="position-relative overflow-hidden bg-white p-4 rounded-4 shadow d-flex align-items-center gap-3 h-100 hc-list-kpi-card">
                <div className={`position-absolute start-0 top-0 h-100 border-start border-5 ${stat.accentClass}`} />
                <div
                  className="d-inline-flex align-items-center justify-content-center rounded-3"
                  style={{ width: 44, height: 44, backgroundColor: stat.iconBg }}
                >
                  {stat.icon}
                </div>
                <div className="d-flex flex-column fw-bold lh-sm">
                  <span className="text-dark" style={{ fontSize: '0.85rem' }}>
                    {stat.label}
                  </span>
                  <span className={`fw-bold fs-5 ${stat.valueClass}`}>
                    {stat.value}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Search, Filter, and Table Container matching other list pages */}
      <div className="bg-white rounded-3 shadow-lg p-3 p-md-4">
        <div className="row g-2 mb-3">
          <div className="col-12 col-md-9">
            <div className="position-relative">
              <span
                className="position-absolute top-50 translate-middle-y d-inline-flex align-items-center justify-content-center text-secondary pe-none"
                style={{ left: '12px' }}
              >
                <MdSearch size={18} />
              </span>
              <input
                type="text"
                className="form-control form-control-sm hc-input"
                placeholder="Search parent insurance, payer ID, or alias name..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                style={{ paddingLeft: '36px', paddingRight: '36px', height: '40px' }}
              />
              {search ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearch('')}
                  className="btn btn-link bg-danger p-0 position-absolute top-50 translate-middle-y text-light rounded-circle"
                  style={{ right: '8px', lineHeight: 0 }}
                >
                  <MdClose size={18} />
                </button>
              ) : null}
            </div>
          </div>

          <div className="col-12 col-md-3">
            <SearchableDropdown
              placeholder="Select service"
              options={SERVICE_FILTER_OPTIONS}
              value={serviceFilter}
              onChange={(val) => setServiceFilter(val || 'All')}
              showSearch={false}
            />
          </div>
        </div>

        <div className="bg-white p-3 rounded shadow">
          {loading ? (
            <div>
              {[...Array(8)].map((_, i) => (
                <div key={i} className="d-flex gap-3 align-items-center mb-3">
                  <div className="skeleton position-relative overflow-hidden rounded-2 w-25" style={{ height: 16 }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '15%' }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '40%' }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: '20%' }} />
                </div>
              ))}
            </div>
          ) : (
            <DataGrid
              columns={columns}
              rows={filteredRows}
              defaultRowsPerPage={10}
              rowsPerPageOptions={[10, 25, 50, 100]}
              stickyHeader
              getRowId={(row) => row.stedi_id}
            />
          )}
        </div>
      </div>

      {/* Aliases Modal for viewing, searching, and 1-click copying recognized names */}
      <Modal
        open={Boolean(aliasModalPayer)}
        onClose={() => {
          setAliasModalPayer(null);
          setAliasModalSearch('');
        }}
        title={aliasModalPayer?.parent_name || 'Insurance Aliases'}
        subtitle={`Payer ID: ${aliasModalPayer?.payer_id || 'N/A'} • ${aliasModalPayer?.child_names?.length || 0} recognized alternate names`}
        size="lg"
        scrollable
      >
        <div className="d-flex flex-column gap-3">
          <div className="position-relative">
            <span
              className="position-absolute top-50 translate-middle-y d-inline-flex align-items-center justify-content-center text-secondary pe-none"
              style={{ left: '12px' }}
            >
              <MdSearch size={18} />
            </span>
            <input
              type="text"
              className="form-control form-control-sm hc-input"
              placeholder="Filter alternate names..."
              value={aliasModalSearch}
              onChange={(e) => setAliasModalSearch(e.target.value)}
              style={{ paddingLeft: '36px', height: '38px' }}
            />
            {aliasModalSearch && (
              <button
                type="button"
                className="btn btn-link p-0 position-absolute top-50 translate-middle-y text-secondary"
                style={{ right: '12px' }}
                onClick={() => setAliasModalSearch('')}
              >
                <MdClose size={16} />
              </button>
            )}
          </div>

          <div className="small text-muted">
            Click any alternate name or the copy icon to copy it to your clipboard for bulk upload spreadsheets:
          </div>

          <div className="row g-2" style={{ maxHeight: '380px', overflowY: 'auto' }}>
            {modalFilteredAliases.map((aliasName, idx) => (
              <div key={idx} className="col-12 col-md-6">
                <button
                  type="button"
                  title="Copy name"
                  className="w-100 p-2 px-3 rounded-3 border d-flex align-items-center justify-content-between gap-2 text-start"
                  style={{ background: 'var(--hc-tint-1)', borderColor: 'var(--hc-surface-border)' }}
                  onClick={() => handleCopyAlias(aliasName)}
                >
                  <span className="small fw-medium text-truncate" style={{ color: 'var(--hc-text-title)' }}>
                    {aliasName}
                  </span>
                  {copiedAlias === aliasName ? (
                    <MdCheck size={16} className="text-success flex-shrink-0" />
                  ) : (
                    <MdContentCopy size={15} className="text-secondary flex-shrink-0" />
                  )}
                </button>
              </div>
            ))}

            {!modalFilteredAliases.length && (
              <div className="text-center py-4 text-muted small">
                No alternate names match "{aliasModalSearch}".
              </div>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
}
